/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: ESM Woocommerce to Hubspot Integration
  Author: Wibert Muza
  Agency URL: https://esminbound.com
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import firebase from 'firebase/app'
import 'firebase/auth'

Vue.use(Router)

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior () {
        return { x: 0, y: 0 }
    },
    routes: [

        {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
            path: '',
            component: () => import('./layouts/main/Main.vue'),
            children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
              {
                path: '/',
                name: 'dashboard',
                component: () => import('./views/Dashboard.vue'),
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', active: true },
                  ],
                  pageTitle: 'Dashboard',
                  authRequired: true
                },
              },
              {
                path: '/sync/configuration',
                name: 'configuration',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'Sync'},
                    { title: 'Configuration', active: true }
                  ],
                  pageTitle: 'Configuration',
                  authRequired: true
                },                
                component: () => import('./views/pages/configuration/Configuration.vue')
              },
              {
                path: '/history',
                name: 'history',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'History', active: true }
                  ],
                  pageTitle: 'History',
                  authRequired: true
                },
                component: () => import('./views/pages/history/History.vue')
              },
              {
                path: '/list/orders',
                name: 'orders-history',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'History'},
                    { title: 'Orders', active: true }
                  ],
                  pageTitle: 'Orders',
                  authRequired: true
                },
                component: () => import('./views/pages/history/OrdersHistory.vue')
              },
              {
                path: '/list/products',
                name: 'products-history',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'History'},
                    { title: 'Products', active: true }
                  ],
                  pageTitle: 'Products',
                  authRequired: true
                },
                component: () => import('./views/pages/history/ProductsHistory.vue')
              },
              {
                path: '/list/customers',
                name: 'customers-history',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'History'},
                    { title: 'Customers', active: true }
                  ],
                  pageTitle: 'Customers',
                  authRequired: true
                },
                component: () => import('./views/pages/history/CustomersHistory.vue') 
              },
              {
                path: '/connections/hubspot',
                name: 'hubspot',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'Connections'},
                    { title: 'Hubspot', active: true }
                  ],
                  pageTitle: 'Hubspot',
                  authRequired: true
                },
                component: () => import('./views/Hubspot.vue')
              },
              {
                path: '/connections/woocommerce',
                name: 'woocommerce',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'Connections'},
                    { title: 'WooCommerce', active: true }
                  ],
                  pageTitle: 'WooCommerce',
                  authRequired: true
                },
                component: () => import('./views/Woocommerce.vue')
              },
              {
                path: '/errors',
                name: 'error',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'Errors', active: true }
                  ],
                  pageTitle: 'Errors',
                  authRequired: true
                },
                component: () => import('./views/pages/errors/Errors.vue')
              },
              {
                path: '/errors/orders',
                name: 'orders-errors',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'Errors'},
                    { title: 'Orders', active: true }
                  ],
                  pageTitle: 'Orders',
                  authRequired: true
                },
                component: () => import('./views/pages/errors/OrdersErrors.vue')
              },
              {
                path: '/errors/products',
                name: 'products-errors',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'Errors'},
                    { title: 'Products', active: true }
                  ],
                  pageTitle: 'Products',
                  authRequired: true
                },
                component: () => import('./views/pages/errors/ProductsErrors.vue')
              },
              {
                path: '/errors/customers',
                name: 'customers-errors',
                meta: {
                  rule: 'editor',
                  breadcrumb: [
                    { title: 'Dashboard', url: '/' },
                    { title: 'Errors'},
                    { title: 'Customers', active: true }
                  ],
                  pageTitle: 'Customers',
                  authRequired: true
                },
                component: () => import('./views/pages/errors/CustomersErrors.vue')
              },
            ],
        },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
        {
            path: '',
            component: () => import('@/layouts/full-page/FullPage.vue'),
            children: [
        // =============================================================================
        // PAGES
        // =============================================================================
              {
                path: '/pages/login',
                name: 'page-login',
                component: () => import('@/views/pages/login/Login.vue'),
                meta: {
                  rule: 'editor'
                } 
              },
              {
                path: '/pages/register',
                name: 'page-register',
                component: () => import('@/views/pages/register/Register.vue'),
                meta: {
                  rule: 'editor'
                } 
              },
              {
                path: '/pages/forgot-password',
                name: 'page-forgot-password',
                component: () => import('@/views/pages/ForgotPassword.vue'),
                meta: {
                  rule: 'editor'
                }
              },
              {
                path: '/pages/reset-password',
                name: 'page-reset-password',
                component: () => import('@/views/pages/ResetPassword.vue'),
                meta: {
                  rule: 'editor'
                }
              },
              {
                path: '/pages/lock-screen',
                name: 'page-lock-screen',
                component: () => import('@/views/pages/LockScreen.vue'),
                meta: {
                  rule: 'editor'
                }
              },
              {
                path: '/pages/comingsoon',
                name: 'page-coming-soon',
                component: () => import('@/views/pages/ComingSoon.vue'),
                meta: {
                  rule: 'editor'
                }
              },
              {
                path: '/pages/error-404',
                name: 'page-error-404',
                component: () => import('@/views/pages/Error404.vue'),
                meta: {
                  rule: 'editor'
                }
              },
              {
                path: '/pages/error-500',
                name: 'page-error-500',
                component: () => import('@/views/pages/Error500.vue'),
                meta: {
                  rule: 'editor'
                }
              },
              {
                path: '/pages/not-authorized',
                name: 'page-not-authorized',
                component: () => import('@/views/pages/NotAuthorized.vue'),
                meta: {
                  rule: 'editor'
                }
              },
              {
                path: '/pages/maintenance',
                name: 'page-maintenance',
                component: () => import('@/views/pages/Maintenance.vue'),
                meta: {
                  rule: 'editor'
                }
              }
            ]
        },
        // Redirect to 404 page, if no match found
        {
            path: '*',
            redirect: '/pages/error-404'
        }
    ],
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
        appLoading.style.display = "none";
    }
})

router.beforeEach((to, from, next) => {
  firebase.auth().onAuthStateChanged(() => {

    // get firebase current user
    const firebaseCurrentUser = firebase.auth().currentUser

    // if (
    //     to.path === "/pages/login" ||
    //     to.path === "/pages/forgot-password" ||
    //     to.path === "/pages/error-404" ||
    //     to.path === "/pages/error-500" ||
    //     to.path === "/pages/register" ||
    //     to.path === "/callback" ||
    //     to.path === "/pages/comingsoon" ||
    //     (auth.isAuthenticated() || firebaseCurrentUser)
    // ) {
    //     return next();
    // }

    // If auth required, check login. If login fails redirect to login page
    if (to.meta.authRequired) {
      if (!(firebaseCurrentUser)) {
        router.push({ path: '/pages/login', query: { to: to.path } })
      }
    }

    if(to.path){
      return next()
    } else {
      router.push({ path: '/' })
    }
    // Specify the current path as the customState parameter, meaning it
    // will be returned to the application after auth
    // auth.login({ target: to.path });

  })

})

export default router
